/**
 * Reference-counted navigation lock over `viewer.navigation.setIsLocked()`, which is a single boolean and therefore
 * breaks with two owners: whichever releases last writes back a value it sampled before the other acquired.
 *
 * State lives on the navigation object under a string key, shared by every copy of this module - extensions are
 * bundled separately, so several copies do coexist, and can even be different versions. Tokens are therefore a
 * fresh object rather than a module-level counter, which would hand out colliding ids across copies; a counter kept
 * in the shared state would do as well, object identity just needs no bookkeeping. Moving all of this onto
 * Navigation itself would drop the shared-key coupling entirely - see the note below.
 *
 * A lock set before the first token is restored on the last release, so an app's own lock survives.
 *
 * Only token holders are protected from each other. A release leaves the flag alone if it did not write it last, so
 * it never re-locks what someone deliberately unlocked; a lock *added* mid-flight is indistinguishable from our own
 * and still gets dropped. Fixing that means funnelling `viewer.setNavigationLock()` through here - intended path is
 * `Navigation.acquireLock()` in core, then deprecating the boolean setter and dropping it in v8, with lock settings
 * and `ToolController`'s lock sharing the same primitive.
 *
 * @example
 *   const release = acquireNavigationLock(viewer.navigation, 'image-overlay/capture');
 *   try { await captureFrame(); } finally { release(); }
 *
 *   withNavigationUnlocked(viewer.navigation, () => viewer.restoreState(state, undefined, true));
 */

const TOKENS_PROPERTY = '__navigationLockTokens'; // defined non-enumerable, so bookkeeping stays out of key scans

function getState(navigation) {
  if (!navigation[TOKENS_PROPERTY]) {
    Object.defineProperty(navigation, TOKENS_PROPERTY, {
      value: { tokens: new Map(), baseLocked: false, lastWritten: false, lockLifted: false },
      enumerable: false, configurable: true, writable: false
    });
  }
  return navigation[TOKENS_PROPERTY];
}

// Single choke point for writes, so `lastWritten` cannot drift from the flag.
function writeLockFlag(navigation, state, locked) {
  navigation.setIsLocked(locked);
  state.lastWritten = locked;
}

/**
 * @param {object} navigation - `viewer.navigation`.
 * @param {string} owner - label for debugging; not required to be unique.
 * @returns {() => void} release, idempotent.
 */
export function acquireNavigationLock(navigation, owner) {
  if (!navigation) return () => {};

  const state = getState(navigation);
  const id = {}; // object identity, not a module-level counter - see the cross-bundle note above

  // While withNavigationUnlocked() is in flight the flag is deliberately false, so sampling it would hand the
  // eventual release an unlock that was never the base state - and a lock the caller had before would be lost.
  if (state.tokens.size === 0) state.baseLocked = state.lockLifted || navigation.getIsLocked();

  state.tokens.set(id, owner);
  writeLockFlag(navigation, state, true);

  return () => {
    if (!state.tokens.delete(id)) return;

    if (state.tokens.size > 0) return;

    // Someone else wrote the flag while we held tokens; their intent is newer than what we sampled.
    if (navigation.getIsLocked() !== state.lastWritten) return;

    writeLockFlag(navigation, state, state.baseLocked);
  };
}

/** Diagnostics only; never installs state. @param {object} navigation @returns {number} */
export function navigationLockCount(navigation) {
  return navigation?.[TOKENS_PROPERTY]?.tokens.size ?? 0;
}

/**
 * Run `task` with the lock lifted, then restore it. Exists for `restoreState()`, which copies position and target
 * unconditionally but routes the up vector and fov through roll-/fov-gated setters that no-op while locked.
 *
 * Tokens are untouched; both writes are recorded as ours. Synchronous by design - an async task would leave the
 * lock open across awaits where the user could navigate.
 *
 * `task` may acquire a token, e.g. through an event handler a camera write fires synchronously, so `lockLifted`
 * marks the flag as not representing anyone's intent for the duration. Note the acquired lock still makes the flag
 * true again for the remainder of `task`, which is inherent to lifting a single boolean rather than gating.
 *
 * @template T
 * @param {object} navigation - `viewer.navigation`.
 * @param {() => T} task
 * @returns {T}
 */
export function withNavigationUnlocked(navigation, task) {
  if (!navigation) return task();

  const state = getState(navigation);
  if (!navigation.getIsLocked()) return task();

  state.lockLifted = true;
  writeLockFlag(navigation, state, false);
  try {
    return task();
  } finally {
    writeLockFlag(navigation, state, true);
    state.lockLifted = false;
  }
}